/**
 * Device Cleanup Utility
 *
 * *force build comment*
 *
 * This module provides comprehensive cleanup functionality for when devices
 * crash, enter error states, or are removed. It coordinates cleanup across
 * multiple subsystems to prevent memory leaks.
 *
 * Memory Leak Prevention:
 * - Clears LiveKit state and event listeners
 * - Clears NewtsConnection message buffers
 * - Clears video component references
 * - Clears broker tracking state
 */

import { toHexFromId, toIdFromHex } from '#~/api/index.js';
import { logger } from '#~/logger/index.js';
import { broker as livekitBroker } from '#~/livekit/index.js';
import { nats } from '#~/nats/index.js';
import type { NewtsConnection } from '#~/newts/connection.js';
import { cleanupAllVideoInstances } from '#~/livekit/components/video/video.js';
import type { LiveKitImpl } from '#~/livekit/livekit.js';
import type { Agent } from '@studio/models/agent';

/**
 * Set to track devices that have been cleaned up to prevent redundant cleanup.
 */
const cleanedUpDevices = new Set<string>();

/**
 * Maximum number of cleaned up device IDs to track before pruning.
 * Prevents this tracking set from becoming a memory leak itself.
 */
const MAX_CLEANUP_TRACKING_SIZE = 1000;

/**
 * Performs comprehensive cleanup for a device that has crashed or entered an error state.
 * This function coordinates cleanup across multiple subsystems to prevent memory leaks.
 *
 * @param agentId - The agent/device ID to clean up
 * @param options - Optional cleanup configuration
 */
export const cleanupDevice = async (
  agentId: Uint8Array,
  options?: {
    /** Whether to also stop the LiveKit connection */
    stopLiveKit?: boolean;
    /** Whether to clear video component references */
    clearVideoRefs?: boolean;
    /** Force cleanup even if already cleaned up recently */
    force?: boolean;
  }
): Promise<void> => {
  const idHex = toHexFromId(agentId);

  if (!idHex) {
    logger.warning('cleanupDevice called with invalid agentId');
    return;
  }

  // Prevent redundant cleanup unless forced
  if (!options?.force && cleanedUpDevices.has(idHex)) {
    logger.debug(`Device ${idHex} already cleaned up recently, skipping`);
    return;
  }

  logger.info(`Starting comprehensive cleanup for device: ${idHex}`);

  try {
    // 1. Clean up LiveKit state (Maps and Records) across all LiveKit instances
    for (const liveKit of livekitBroker) {
      const livekitImpl = liveKit as LiveKitImpl;
      if (livekitImpl) {
        // Optionally stop the local LiveKit connection
        if (options?.stopLiveKit && typeof livekitImpl.stopLocal === 'function') {
          try {
            await livekitImpl.stopLocal(agentId);
          } catch (error) {
            logger.warning(`Error stopping local LiveKit for ${idHex}: ${error}`);
          }
        }
      }
    }

    // 2. Clean up NewtsConnection message buffer
    try {
      const newtsConnection = nats.agentConnection(idHex) as NewtsConnection | undefined;
      if (newtsConnection && typeof newtsConnection.clearMsgBuffer === 'function') {
        newtsConnection.clearMsgBuffer();
      }
    } catch (error) {
      logger.warning(`Error clearing NewtsConnection buffer for ${idHex}: ${error}`);
    }

    // 3. Optionally clean up video component references
    if (options?.clearVideoRefs) {
      cleanupAllVideoInstances();
    }

    // Track that this device has been cleaned up
    cleanedUpDevices.add(idHex);

    // Prune tracking set if it gets too large
    if (cleanedUpDevices.size > MAX_CLEANUP_TRACKING_SIZE) {
      const iterator = cleanedUpDevices.values();
      // Remove oldest entries (first half)
      for (let i = 0; i < MAX_CLEANUP_TRACKING_SIZE / 2; i++) {
        const value = iterator.next().value;
        if (value) {
          cleanedUpDevices.delete(value);
        }
      }
    }

    logger.info(`Completed cleanup for device: ${idHex}`);
  } catch (error) {
    logger.error(`Error during device cleanup for ${idHex}: ${error}`);
  }
};

/**
 * Cleans up a device by its hex ID string.
 * Convenience wrapper for cleanupDevice.
 *
 * @param agentIdHex - The agent/device ID as hex string
 * @param options - Optional cleanup configuration
 */
export const cleanupDeviceByHex = async (
  agentIdHex: Agent['id'],
  options?: Parameters<typeof cleanupDevice>[1]
): Promise<void> => {
  const agentId = toIdFromHex(agentIdHex);
  if (agentId) {
    await cleanupDevice(agentId, options);
  }
};

/**
 * Clears the cleanup tracking set.
 * Call this when resetting the application state.
 */
export const resetCleanupTracking = (): void => {
  cleanedUpDevices.clear();
};
